// Fire-and-forget transport for frontend analytics events.
//
// Design constraints:
//   - Must never break widget mounting or chat rendering if delivery fails.
//   - Must work identically in Vite dev (`src/main.js`) and the IIFE embed.
//   - Quiet in production (`debugAnalytics: false`); verbose when enabled.
//   - Prefers `navigator.sendBeacon` for unload-sensitive events, falls back
//     to `fetch({ keepalive: true })` with an AbortController timeout.

import { buildEvent } from './events.js'
import { newClientActionId } from './ids.js'

const SEND_TIMEOUT_MS = 3000

let configured = {
  analyticsEndpoint: '',
  analyticsEnabled: true,
  environment: undefined,
  debugAnalytics: false,
}

/**
 * Apply resolved config to the transport. Call once during mount, after
 * `resolveConfig(overrides)` has produced the merged config.
 * @param {{ analyticsEndpoint?: string, analyticsEnabled?: boolean, environment?: string, debugAnalytics?: boolean }} cfg
 */
export function configureAnalytics(cfg = {}) {
  configured = {
    analyticsEndpoint: typeof cfg.analyticsEndpoint === 'string' ? cfg.analyticsEndpoint : '',
    analyticsEnabled: cfg.analyticsEnabled !== false,
    environment: cfg.environment,
    debugAnalytics: cfg.debugAnalytics === true,
  }
}

export function getAnalyticsConfig() {
  return { ...configured }
}

function isEnabled() {
  return Boolean(configured.analyticsEnabled && configured.analyticsEndpoint)
}

function debug(...args) {
  if (configured.debugAnalytics) {
    console.info('[Fintepla][analytics]', ...args)
  }
}

function emitCustomEvent(envelope) {
  if (typeof window === 'undefined') return
  if (typeof window.CustomEvent !== 'function' || typeof window.dispatchEvent !== 'function') return
  try {
    window.dispatchEvent(new window.CustomEvent('fintepla:analytics', { detail: envelope }))
  } catch {
    // swallow — purely a debug bridge
  }
}

function postViaFetch(endpoint, body) {
  try {
    const f = typeof globalThis !== 'undefined' ? globalThis.fetch : null
    if (typeof f !== 'function') return false
    const ctrl =
      typeof AbortController === 'function' ? new AbortController() : null
    const timer = ctrl
      ? setTimeout(() => {
          try {
            ctrl.abort()
          } catch {
            // noop
          }
        }, SEND_TIMEOUT_MS)
      : null
    const p = f(endpoint, {
      method: 'POST',
      mode: 'cors',
      credentials: 'omit',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body,
      signal: ctrl ? ctrl.signal : undefined,
    })
    if (p && typeof p.then === 'function') {
      p.then(
        () => timer && clearTimeout(timer),
        (err) => {
          if (timer) clearTimeout(timer)
          debug('fetch rejected (swallowed)', err)
        },
      )
    }
    return true
  } catch (err) {
    debug('fetch threw (swallowed)', err)
    return false
  }
}

function postViaBeacon(endpoint, body) {
  try {
    const nav = typeof navigator !== 'undefined' ? navigator : null
    if (!nav || typeof nav.sendBeacon !== 'function') return false
    // Some collectors refuse non-text beacons. Try JSON first, fall back to
    // text/plain which is always allowed as a CORS-safelisted content type.
    try {
      const blob = new Blob([body], { type: 'application/json' })
      if (nav.sendBeacon(endpoint, blob)) return true
    } catch {
      // fall through
    }
    try {
      const blob = new Blob([body], { type: 'text/plain;charset=UTF-8' })
      return nav.sendBeacon(endpoint, blob) === true
    } catch {
      return false
    }
  } catch {
    return false
  }
}

function shouldUseBeacon(opts) {
  if (opts && opts.beacon === true) return true
  if (typeof document === 'undefined') return false
  return document.visibilityState === 'hidden'
}

/**
 * Build and dispatch an analytics event. Always resolves; never throws.
 *
 * @param {import('./events.js').FrontendAnalyticsEventType} eventType
 * @param {object} [fields]  Optional `{ correlation, message, metadata, context }`.
 * @param {{ beacon?: boolean }} [opts]
 */
export function track(eventType, fields = {}, opts = {}) {
  let envelope
  try {
    const explicitClientActionId =
      fields?.correlation && typeof fields.correlation.client_action_id === 'string'
        ? fields.correlation.client_action_id
        : null
    const clientActionId = explicitClientActionId || newClientActionId()

    envelope = buildEvent(
      eventType,
      {
        environment: configured.environment,
        ...fields,
        correlation: {
          ...(fields?.correlation || {}),
          client_action_id: clientActionId,
        },
      },
      clientActionId,
    )
  } catch (err) {
    debug('buildEvent threw (swallowed)', err)
    return
  }

  debug(eventType, envelope)
  emitCustomEvent(envelope)

  if (!isEnabled()) return

  let body
  try {
    body = JSON.stringify(envelope)
  } catch (err) {
    debug('JSON.stringify threw (swallowed)', err)
    return
  }

  const endpoint = configured.analyticsEndpoint
  if (shouldUseBeacon(opts)) {
    if (postViaBeacon(endpoint, body)) return
  }
  if (!postViaFetch(endpoint, body)) {
    // Last-ditch beacon if fetch was unavailable.
    postViaBeacon(endpoint, body)
  }
}

